/**
 * autoSave — debounced auto-save hook (issue #437).
 *
 * Wraps an async save function in a `delayMs` debounce so the
 * SLP's edits flush to the server shortly after they stop
 * typing. The hook:
 *
 *   - Schedules a save only after `content` actually differs
 *     from the last persisted value, so the hook does NOT fire
 *     a redundant save on mount or when `enabled` flips to true
 *     while the content is still the loaded-from-server baseline
 *     (issue #492).
 *   - Schedules a save whenever `content` changes while a
 *     previous schedule is pending (the previous timer is
 *     cancelled so only the latest content is persisted).
 *   - Returns `{ flush, reset, status, lastSavedAt, error }` so
 *     React components can re-render on state transitions.
 *   - Exposes `flush()` for callers that need to force a save
 *     synchronously (e.g. before navigating away).
 *
 * The hook keeps the latest `content` + `save` in refs so the
 * timer closure always saves the latest value even if the caller
 * didn't memoize `save`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface UseAutoSaveOptions<T> {
  /** The content to persist. Changes schedule a debounced save. */
  content: T;
  /** Async save function. Receives the content as its only arg. */
  save: (content: T) => Promise<void>;
  /** Debounce delay in ms. Default 2000. */
  delayMs?: number;
  /** Disable scheduling (e.g. while the editor is read-only). */
  enabled?: boolean;
  /**
   * Equality comparator used to decide whether `content` is
   * different from the last-persisted baseline (issue #492).
   * Defaults to `Object.is`, which works for primitives like the
   * string content used by the Session Planner. Pass a deep
   * equality fn if your caller constructs a fresh object / array
   * on every render — otherwise the hook will schedule a save
   * on every render even when the underlying value is unchanged.
   */
  isEqual?: (a: T, b: T) => boolean;
}

export interface UseAutoSaveResult {
  /** Force an immediate save of the current content. */
  flush: () => Promise<void>;
  /** Reset to idle (e.g. when switching plans). */
  reset: () => void;
  /** Latest status. */
  status: AutoSaveStatus;
  /** Unix-ms timestamp of the last successful save, or null. */
  lastSavedAt: number | null;
  /** Last error message, or null. */
  error: string | null;
}

/**
 * useAutoSave — debounced auto-save.
 *
 * Schedules `save(content)` `delayMs` after the most recent
 * `content` change. Returns reactive state + a `flush()` handle.
 *
 * Issue #492: the hook now skips scheduling entirely when
 * `content` is equal (by `Object.is`) to the last persisted
 * value. This avoids the redundant save that previously fired
 * 2s after mount / after `enabled` flipped to true even when
 * the user had typed nothing.
 */
export function useAutoSave<T>({
  content,
  save,
  delayMs = 2000,
  enabled = true,
  isEqual,
}: UseAutoSaveOptions<T>): UseAutoSaveResult {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<number | null>(null);
  const latestContentRef = useRef<T>(content);
  const latestSaveRef = useRef(save);
  const isEqualRef = useRef<(a: T, b: T) => boolean>(isEqual ?? Object.is);
  // Tracks the content value the hook last persisted (or
  // observed as the baseline on mount / enable / reset). The
  // gate that decides whether to schedule a save runs the
  // comparator against this ref (issue #492).
  const lastPersistedRef = useRef<T>(content);

  // Keep the latest closures / values in refs so the timer
  // always runs against the latest content + save fn (issue
  // #538). Refs are updated inside `useEffect` rather than in
  // the render body so the hook is safe under React concurrent
  // rendering / StrictMode double-render.
  useEffect(() => {
    latestContentRef.current = content;
  }, [content]);
  useEffect(() => {
    latestSaveRef.current = save;
  }, [save]);
  useEffect(() => {
    isEqualRef.current = isEqual ?? Object.is;
  }, [isEqual]);

  const runSave = useCallback(async (snapshot: T): Promise<void> => {
    setStatus('saving');
    setError(null);
    try {
      await latestSaveRef.current(snapshot);
      lastPersistedRef.current = snapshot;
      setLastSavedAt(Date.now());
      setStatus('saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setStatus('error');
    }
  }, []);

  // Schedule a debounced save whenever content changes.
  //
  // Issue #492: bail out when `content` still matches the last
  // persisted snapshot. This prevents the redundant save that
  // would otherwise fire on mount and when `enabled` flips to
  // true while the loaded content is unchanged. The status
  // stays at whatever it was (idle / saved) instead of
  // momentarily flipping to 'pending' and back.
  //
  // Issue #538: the gate runs the user-supplied `isEqual`
  // comparator (defaulting to `Object.is`) so callers passing
  // a freshly-constructed object/array each render can supply
  // a deep-equality fn and avoid scheduling a save on every
  // render.
  useEffect(() => {
    if (!enabled) return;
    if (isEqualRef.current(content, lastPersistedRef.current)) return;
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setStatus('pending');
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void runSave(content);
    }, delayMs);
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // We intentionally only depend on `content` + `enabled` +
    // `delayMs` so changing the `save` fn reference doesn't
    // reset the debounce mid-flight.
  }, [content, enabled, delayMs, runSave]);

  const flush = useCallback(async (): Promise<void> => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await runSave(latestContentRef.current);
  }, [runSave]);

  const reset = useCallback((): void => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Reset the persistence baseline to whatever the caller has
    // now so a stale comparison can't trigger a spurious save
    // after reset() (issue #492).
    lastPersistedRef.current = latestContentRef.current;
    setStatus('idle');
    setLastSavedAt(null);
    setError(null);
  }, []);

  return { flush, reset, status, lastSavedAt, error };
}
